import { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';

import { getPaymentStatus } from '../../../../api/paymentService';
import { useOptionalTournament } from '../../../../context/TournamentContext';
import type { Payment, GetPaymentStatusResponse } from '../../../../types/payment';

/**
 * Payment session data stored in localStorage
 */
export interface PaymentSessionData {
  paymentId: string;
  tournamentId: string;
  userId: string;
  createdAt: string;
}

/**
 * Options for usePaymentSession hook
 */
export interface UsePaymentSessionOptions {
  /** Payment ID to hydrate */
  paymentId: string;
  /** Callback when payment ownership validation fails */
  onOwnershipError?: () => void;
  /** Callback when API fetch fails */
  onFetchError?: (error: Error) => void;
}

/**
 * Result returned by usePaymentSession hook
 */
export interface UsePaymentSessionResult {
  /** Current payment data */
  payment: GetPaymentStatusResponse | null;
  /** Tournament ID associated with payment */
  tournamentId: string | null;
  /** Loading state */
  isLoading: boolean;
  /** Hydration error */
  error: Error | null;
  /** Whether ownership was validated */
  isOwnershipValid: boolean;
  /** Refetch payment data */
  refetch: () => Promise<void>;
}

/**
 * localStorage key for payment session
 */
const getSessionKey = (paymentId: string): string =>
  `payment_session_${paymentId}`;

/**
 * Get payment session from localStorage
 */
const getStoredSession = (paymentId: string): PaymentSessionData | null => {
  try {
    const stored = localStorage.getItem(getSessionKey(paymentId));
    if (!stored) return null;
    return JSON.parse(stored) as PaymentSessionData;
  } catch {
    return null;
  }
};

/**
 * Save payment session to localStorage
 */
export const savePaymentSession = (data: PaymentSessionData): void => {
  try {
    localStorage.setItem(getSessionKey(data.paymentId), JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save payment session:', error);
  }
};

/**
 * Remove payment session from localStorage
 */
export const clearPaymentSession = (paymentId: string): void => {
  try {
    localStorage.removeItem(getSessionKey(paymentId));
  } catch (error) {
    console.error('Failed to clear payment session:', error);
  }
};

/**
 * Get current user ID from cookies or localStorage
 * Exported for testing purposes
 */
export const getCurrentUserId = (): string | null => {
  const cookieUserId = Cookies.get('userId');
  const localStorageUserId = localStorage.getItem('userId');
  return cookieUserId || localStorageUserId || null;
};

/**
 * usePaymentSession Hook
 * 
 * Hydrates payment session data with priority:
 * 1. TournamentContext (if currentPayment matches)
 * 2. localStorage (cached session)
 * 3. API fetch (fallback)
 * 
 * Also validates payment ownership against current user.
 * 
 * Task 9.2: Session hydration and ownership validation
 */
export function usePaymentSession(
  options: UsePaymentSessionOptions
): UsePaymentSessionResult {
  const { paymentId, onOwnershipError, onFetchError } = options;

  const tournamentContext = useOptionalTournament();

  const [payment, setPayment] = useState<GetPaymentStatusResponse | null>(null);
  const [tournamentId, setTournamentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isOwnershipValid, setIsOwnershipValid] = useState(true);

  /**
   * Validate payment ownership
   */
  const validateOwnership = useCallback(
    (paymentData: GetPaymentStatusResponse): boolean => {
      const currentUserId = getCurrentUserId();
      
      // If payment has userId, validate it matches current user
      if (paymentData.userId && currentUserId && paymentData.userId !== currentUserId) {
        console.error('Payment ownership mismatch:', {
          paymentUserId: paymentData.userId,
          currentUserId,
        });
        return false;
      }
      
      return true;
    },
    []
  );

  /**
   * Fetch payment from API
   */
  const fetchPayment = useCallback(async (): Promise<GetPaymentStatusResponse | null> => {
    if (!paymentId) {
      return null;
    }

    try {
      const response = await getPaymentStatus(paymentId);
      return response;
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error('Failed to fetch payment');
      setError(fetchError);
      onFetchError?.(fetchError);
      return null;
    }
  }, [paymentId, onFetchError]);

  /**
   * Hydrate payment session
   */
  const hydrate = useCallback(async () => {
    if (!paymentId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Priority 1: Check TournamentContext
      const contextPayment = tournamentContext?.currentPayment;
      if (contextPayment && contextPayment.id === paymentId) {
        const isValid = validateOwnership(contextPayment);
        setIsOwnershipValid(isValid);
        
        if (!isValid) {
          onOwnershipError?.();
        }
        
        setPayment(contextPayment);
        setTournamentId(String(contextPayment.tournamentId || tournamentContext?.tournamentId || ''));
        setIsLoading(false);
        return;
      }

      // Priority 2: Check localStorage
      const storedSession = getStoredSession(paymentId);
      if (storedSession) {
        setTournamentId(storedSession.tournamentId);
      }

      // Priority 3: Fetch from API
      const fetchedPayment = await fetchPayment();
      
      if (fetchedPayment) {
        const isValid = validateOwnership(fetchedPayment);
        setIsOwnershipValid(isValid);
        
        if (!isValid) {
          onOwnershipError?.();
        }
        
        setPayment(fetchedPayment);
        
        // Update tournamentId from fetched payment if not already set
        if (!tournamentId && fetchedPayment.tournamentId) {
          setTournamentId(fetchedPayment.tournamentId);
        }
      }
    } catch (err) {
      const hydrateError = err instanceof Error ? err : new Error('Hydration failed');
      setError(hydrateError);
    } finally {
      setIsLoading(false);
    }
  }, [
    paymentId,
    tournamentContext,
    validateOwnership,
    fetchPayment,
    onOwnershipError,
    tournamentId,
  ]);

  /**
   * Refetch payment data
   */
  const refetch = useCallback(async () => {
    if (!paymentId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const fetchedPayment = await fetchPayment();
    
    if (fetchedPayment) {
      const isValid = validateOwnership(fetchedPayment);
      setIsOwnershipValid(isValid);
      
      if (!isValid) {
        onOwnershipError?.();
      }
      
      setPayment(fetchedPayment);
      
      if (fetchedPayment.tournamentId) {
        setTournamentId(fetchedPayment.tournamentId);
      }
    }

    setIsLoading(false);
  }, [paymentId, fetchPayment, validateOwnership, onOwnershipError]);

  // Initial hydration
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return {
    payment,
    tournamentId,
    isLoading,
    error,
    isOwnershipValid,
    refetch,
  };
}

export default usePaymentSession;
